import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  trackInitialValues,
  TrackTabs,
} from 'components/organisms/forms/track/TrackForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiVinylRecord, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostTrack } from 'services/tracks';
import {
  usePostTrackChunkedFile,
  usePostTrackFile,
} from 'services/tracks/file';
import { ITrackMutationForm } from 'services/tracks/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const CreateTrack = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createTrack = usePostTrack();
  const addTrackFile = usePostTrackFile();
  const addTrackChunkedFile = usePostTrackChunkedFile();
  const mutateOptions = useMutateOptions<ITrackMutationForm>();
  const { pathname } = useLocation();

  const onGoToDetail = (id: string) =>
    navigate(`/track/${id}/${getLastPathname(pathname)}`);

  const onCreateTrackFile = async (file: File, trackId: string, version: number) => {
    await addTrackChunkedFile.mutateAsync(
      { file, trackId, version },
      {
        onError: error => {
          onErrorShowToast(error, 'toast-message.add-file');
          onGoToDetail(trackId);
        },
        onSuccess: () => onGoToDetail(trackId),
      }
    );
  };

  const onCreateTrack = async (
    values: ITrackMutationForm,
    helpers: FormikHelpers<ITrackMutationForm>
  ) => {
    const track = {
      ...middleware.track.formToQuery(values),
      waitingForFile: !!values.file,
    };

    await createTrack.mutateAsync(track, {
      ...mutateOptions(helpers),
      onSuccess: track =>
        !!values.file
          ? onCreateTrackFile(values.file, track.id, track.version)
          : onGoToDetail(track.id),
    });
  };

  return (
    <Form.Track
      i18nextPrefix='track'
      initialValues={trackInitialValues}
      onSubmit={onCreateTrack}
      tittle={t('create-track.topbar.title')}
      disableTabs={[TrackTabs.statistics, TrackTabs.products, TrackTabs.fingerprints]}
      customBackItem={
        <Button type='button' onlyIcon onClick={() => navigate('/track')}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.create')} />
          <Button.Icon icon={<PiVinylRecord />} />
        </Button>
      }
    />
  );
};

export default CreateTrack;
